// frontend/src/components/book/PendingSection.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { RefreshCw, Search, X } from 'lucide-react';
import { api } from '../../api';
import { colors, fontFamily } from '../../theme';
import ToastHost, { useToast } from '../shared/Toast';

const settleKey = (b) => (b.is_parlay_leg ? `leg_${b.leg_id || b.id}` : `bet_${b.id}`);

export default function PendingSection() {
  const { toast, showToast } = useToast();
  const [pendingGames, setPendingGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('user');
  const [sortDir, setSortDir] = useState('asc');
  const [search, setSearch] = useState('');
  const [selectedBet, setSelectedBet] = useState(null);
  const [settling, setSettling] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async (bypassCache = false) => {
    setLoading(true);
    try {
      if (bypassCache) await api.refreshPendingScores();
      const data = await api.getPendingSettlement(bypassCache);
      // The backend splits games into `completed_games` (ESPN/GoalServe confirms
      // the game is over) and `pending_games` (not yet confirmed complete). Both
      // sets still hold bets with status='pending' in the DB and still need manual
      // settlement here, so combine them rather than only showing `pending_games`
      // -- otherwise a game would vanish from this list the moment a Refresh call
      // marks it "completed", even though its bets are still unsettled.
      setPendingGames([...(data.completed_games || []), ...(data.pending_games || [])]);
    } catch (err) {
      console.error('Failed to load pending bets:', err);
    } finally {
      setLoading(false);
    }
  };

  // Flatten "games with bets" into one row per bet.
  const rows = useMemo(() => {
    const flat = [];
    for (const game of pendingGames) {
      const matchup = game.away_team && game.home_team
        ? `${game.away_team} @ ${game.home_team}`
        : (game.game_description || '');
      for (const bet of (game.all_bets || [])) {
        flat.push({
          rowKey: `${bet.is_parlay_leg ? 'leg' : 'bet'}_${bet.leg_id || bet.id}`,
          user: bet.user_name || 'Unknown',
          description: bet.description,
          amount: bet.amount,
          odds: bet.odds,
          matchup,
          isParlayLeg: !!bet.is_parlay_leg,
          bet
        });
      }
    }
    const q = search.trim().toLowerCase();
    const filtered = q
      ? flat.filter(r =>
          r.user.toLowerCase().includes(q) ||
          String(r.description || '').toLowerCase().includes(q) ||
          r.matchup.toLowerCase().includes(q))
      : flat;
    const sorted = [...filtered].sort((a, b) => {
      if (sortField === 'amount') {
        const diff = Number(a.amount ?? 0) - Number(b.amount ?? 0);
        return sortDir === 'asc' ? diff : -diff;
      }
      const av = String(a[sortField] ?? '').toLowerCase();
      const bv = String(b[sortField] ?? '').toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [pendingGames, sortField, sortDir, search]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const settleSelected = async (outcome) => {
    if (!selectedBet) return;
    setSettling(true);
    try {
      const result = await api.batchSettle({ [settleKey(selectedBet.bet)]: outcome });
      if (result.errors && result.errors.length > 0) {
        showToast(result.errors[0], 'error');
      } else {
        showToast(`Bet settled — ${outcome.toUpperCase()}`);
      }
      setSelectedBet(null);
      await load();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to settle bet', 'error');
    } finally {
      setSettling(false);
    }
  };

  if (loading) {
    return <p style={{ color: colors.textMuted, fontFamily }}>Loading pending bets...</p>;
  }

  return (
    <div style={{ color: colors.textPrimary, fontFamily }}>
      <ToastHost toast={toast} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', gap: '1rem', flexWrap: 'wrap' }}>
        <h2 style={{ color: colors.textPrimary, margin: 0 }}>Pending ({rows.length})</h2>
        <button
          onClick={() => load(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: colors.goldActiveBg, border: `1px solid ${colors.gold}`, color: colors.textPrimary, borderRadius: 6, padding: '0.5rem 1rem', cursor: 'pointer', fontFamily }}
        >
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      <div style={{ position: 'relative', marginBottom: '1rem', maxWidth: 360 }}>
        <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: colors.textMuted }} />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by customer, bet, or matchup..."
          style={{ width: '100%', padding: '0.5rem 0.75rem 0.5rem 2rem', background: colors.surfaceBg, border: `1px solid ${colors.goldMuted}`, borderRadius: 6, color: colors.textPrimary, fontFamily, fontSize: '0.85rem', boxSizing: 'border-box' }}
        />
      </div>

      {rows.length === 0 ? (
        <p style={{ color: colors.textMuted }}>No pending bets.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${colors.goldMuted}` }}>
                <th onClick={() => toggleSort('user')} style={{ color: colors.textMuted, padding: '0.5rem', textAlign: 'left', cursor: 'pointer' }}>
                  User {sortField === 'user' && (sortDir === 'asc' ? '▲' : '▼')}
                </th>
                <th style={{ color: colors.textMuted, padding: '0.5rem', textAlign: 'left' }}>Bet</th>
                <th onClick={() => toggleSort('amount')} style={{ color: colors.textMuted, padding: '0.5rem', textAlign: 'right', cursor: 'pointer' }}>
                  Amount {sortField === 'amount' && (sortDir === 'asc' ? '▲' : '▼')}
                </th>
                <th style={{ color: colors.textMuted, padding: '0.5rem', textAlign: 'center' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.rowKey} style={{ borderBottom: `1px solid ${colors.sectionBg}` }}>
                  <td style={{ padding: '0.5rem' }}>{row.user}</td>
                  <td style={{ padding: '0.5rem' }}>
                    {row.description}
                    {row.isParlayLeg && <span style={{ marginLeft: 6, fontSize: '0.7rem', color: colors.freePlay }}>(parlay leg)</span>}
                  </td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>${Number(row.amount).toFixed(2)}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                    <button
                      onClick={() => setSelectedBet(row)}
                      style={{ background: colors.goldActiveBg, border: `1px solid ${colors.gold}`, color: colors.textPrimary, borderRadius: 4, padding: '0.3rem 0.8rem', cursor: 'pointer', fontSize: '0.8rem', fontFamily }}
                    >
                      Settle
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedBet && (
        <div
          onClick={() => !settling && setSelectedBet(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '1rem' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: colors.bgGradient, border: `1px solid ${colors.goldMuted}`, borderRadius: 10, padding: '1.5rem', maxWidth: 420, width: '100%', boxShadow: colors.shadowRaised }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, color: colors.textPrimary }}>Settle Bet</h3>
              <button onClick={() => !settling && setSelectedBet(null)} style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ background: colors.surfaceBg, border: `1px solid ${colors.goldMuted}`, borderRadius: 6, padding: '0.75rem', marginBottom: '1rem', fontSize: '0.85rem' }}>
              <div style={{ fontWeight: 700 }}>{selectedBet.user}</div>
              {selectedBet.matchup && <div style={{ color: colors.textMuted }}>{selectedBet.matchup}</div>}
              <div style={{ marginTop: 4 }}>{selectedBet.description}</div>
              <div style={{ color: colors.textMuted, marginTop: 4 }}>
                ${Number(selectedBet.amount).toFixed(2)} @ {selectedBet.odds}
                {selectedBet.isParlayLeg && ' · parlay leg (parlay re-prices)'}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
              {[
                ['won', 'Won', colors.odds],
                ['lost', 'Lost', colors.negative],
                ['push', 'Push', colors.textMuted],
                ['void', 'Void', colors.pending],
              ].map(([outcome, label, color]) => (
                <button
                  key={outcome}
                  disabled={settling}
                  onClick={() => settleSelected(outcome)}
                  style={{ padding: '0.6rem', borderRadius: 6, border: `1px solid ${color}`, background: 'transparent', color, fontWeight: 700, fontFamily, cursor: settling ? 'not-allowed' : 'pointer', opacity: settling ? 0.5 : 1 }}
                >
                  {label}
                </button>
              ))}
            </div>
            <p style={{ color: colors.textFaint, fontSize: '0.72rem', marginTop: '0.85rem', marginBottom: 0 }}>
              Push and Void both refund the stake with no win/loss recorded.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
